import {inject, Injectable} from '@angular/core';
import {Location} from '@angular/common';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {StateService} from '@uirouter/core';
import {UIRouter} from "@uirouter/angular";

import {AppAlertService} from './app-alert.service';
import {DatabaseService} from './database.service';
import {MenuService} from './menu.service';
import {ParametersService} from './parameters.service';
import {PopupService} from "./popup.service";
import {SettingsService, SettingsNotificationsService, SettingsModuleInfoService} from './settings';
import {StylesService} from './style.service';
import {SubtitlesService} from './subtitles.service';
import {UpdateService} from "./update.service";
import {UserService} from './user.service';
import {ViewsService} from './views.service'
import {ZonesService} from "./zones.service";
import {ZonesSystemService} from "./zones-system.service";
import {ModuleData} from "../models";

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private appAlert = inject(AppAlertService);
    private database = inject(DatabaseService);
    private http = inject(HttpClient);
    private location = inject(Location);
    private menu = inject(MenuService);
    private parameters = inject(ParametersService);
    private popup = inject(PopupService);
    private router = inject(UIRouter);
    private settings = inject(SettingsService);
    private settingsModuleInfo = inject(SettingsModuleInfoService);
    private settingsNotifications = inject(SettingsNotificationsService);
    private $state = inject(StateService);
    private styles = inject(StylesService);
    private subtitles = inject(SubtitlesService);
    private update = inject(UpdateService);
    private user = inject(UserService);
    private views = inject(ViewsService);
    private zones = inject(ZonesService);
    private zonesSystem = inject(ZonesSystemService);

    username: string = '';
    password: string = '';
    rememberMe: boolean = false;
    restApiHash: string = '';
    remote: boolean = false;
    adminName: string = '';
    adminPassword: string = '';
    token: string = '';
    private _authenticated = false;

    login(): void {
        this.parameters.showLoginBox = false;
        this.parameters.redirecting = true;
        this.views.hideView();
        let data = {};

        if (this.router.globals.params['client_id']) {
            data = {
                username: this.username,
                password: this.password,
                clientId: this.router.globals.params['client_id'],
            };
        } else if (this.location.path() === '/login') {
            data = {
                username: this.username,
                password: this.password,
                rememberMe: this.rememberMe,
                languageId: this.subtitles.language,
                remote: this.remote
            };
        } else if (this.remote) {
            data = {username: this.restApiHash, remote: this.remote};
        } else {
            data = {
                username: this.username,
                adminName: this.adminName,
                adminPassword: this.adminPassword,
                languageId: this.subtitles.language,
            };
        }

        if (this.router.globals.params['client_id']) {
            this.http.post('/api/v1/authentication', data).subscribe((response: any) => {
                let redirectUrl = this.router.globals.params['redirect_uri'];
                if (response.authenticated) {
                    if (response.access_google_home) {
                        redirectUrl += '?code=' + response.token;
                        redirectUrl += '&state=' + this.router.globals.params['state'];
                    } else {
                        redirectUrl += '#state=' + this.router.globals.params['state'];
                        redirectUrl += '&access_token=' + response.token;
                        redirectUrl += '&token_type=Bearer';
                    }
                    window.location.href = redirectUrl;
                } else {
                    // Only needed here to stop the loading animation.
                    // On redirect, it'll stay until the page is redirected
                    this.parameters.redirecting = false;
                    this.showErrors(response);
                }
            });
        } else {
            this.http.post('/api/v1/authentication', data).subscribe({
                next: (response: any) => {
                    this.handleLogin(response);

                    this.http.get<ModuleData[]>(`/api/v1/users/${this.user.user_id}/modules`).subscribe((response) => {
                        this.handleModulesList(response);
                    });
                },
                error: (error: HttpErrorResponse) => {
                    const errorData = error.error.error;
                    this.showErrors(errorData);
                }
            });

            this.parameters.redirecting = false;
        }
    }

    private showErrors(errorData: any): void {
        this.views.showView();

        let message = '<div class="text-align-center">';
        if (errorData.invalidClient) {
            if (this.subtitles.language === 'pl') {
                message += `Ups! Coś poszło nie tak. Proszę spróbuj ponownie.</div>`;
            } else {
                message += `Oops! Something went wrong. Please try again.</div>`;
            }
            this.appAlert.showAppAlert(message, this.subtitles.setSubtitle(475), this.parameters.showLoginBoxFunction.bind(this.parameters), false, null);
        } else if (errorData.accountStatus === 'blocked') {
            // message = this.subtitles.setSubtitle(3820); // do crawlera napisów
            if (this.subtitles.language === 'pl') {
                message += `Zbyt wiele nieudanych prób logowania. Spróbuj ponownie za 10 minut lub wygeneruj link do zmiany hasła.</div>`;
            } else {
                message += `Too many login attempts. Please try again in 10 minutes or generate password reset link.</div>`;
            }
            this.appAlert.showAppAlert(message, this.subtitles.setSubtitle(475), this.parameters.showLoginBoxFunction.bind(this.parameters), false, null);
        } else if (errorData.active === false) {
            message += `${this.subtitles.setSubtitle(3395)}</div>`; // konto nieaktywne
            this.appAlert.showAppAlert(message, this.subtitles.setSubtitle(1136), this.parameters.showLoginBoxFunction.bind(this.parameters), false, null);
        } else {
            message += `${this.subtitles.setSubtitle(1135)}</div>`; // nieprawidłowe dane
            this.appAlert.showAppAlert(message, this.subtitles.setSubtitle(1136), this.parameters.showLoginBoxFunction.bind(this.parameters), false, null);
        }

        this.password = '';
        this.adminPassword = '';
    }

    remoteLogout(): void {
        this.stopUpdaters();
        this.remote = false;
        this.restApiHash = '';
        this.resetLoginFormData();
        this.resetUserData();
        this.subtitles.getSubtitles('default');
    }

    logout(): void {
        this.remoteLogout();
        this.styles.setDefaultStyle();
        this.views.showView();
        this.$state.go('login');
    }

    private handleModulesList(response: ModuleData[]): void {
        this.setModules(response);
        this.views.showView();
        this.database.getModuleData().subscribe();
        this.menu.getMainMenu();
        this.update.startInterval();
        this.popup.startPopupInterval();
        this.$state.go('home', {selectedModuleHash: this.parameters.selectedModuleHash});
    }

    private handleLogin(response: any): void {
        this._authenticated = true;
        this.user.username = this.username;
        this.user.user_id = response.user_id;
        this.token = response.token;
        window.localStorage.setItem('token', response.token);

        if (!this.parameters.demoAccount()) {
            setTimeout(() => {
                this.settingsNotifications.addNotificationDevice();
            }, 1000);
        }
    }

    private setModules(response: ModuleData[]): void {
        let style: string = '';
        let type: string = '';
        let moduleIndex: number = 0;

        this.parameters.modules = response;
        if (this.parameters.modules.length > 0) {
            this.settingsModuleInfo.setFirstModuleAsDefaultIfNeeded(response);

            this.parameters.modules.forEach((module: any, index: number) => {
                if (module.default) {
                    moduleIndex = index;
                    this.parameters.selectedModuleHash = module.udid;
                }
            });
            window.localStorage.setItem('selectedModuleHash', this.parameters.selectedModuleHash.toString());

            style = this.parameters.modules[moduleIndex].style;
            type = this.parameters.modules[moduleIndex].type;

            this.parameters.selectedModuleIndex = moduleIndex;
            this.parameters.selectedModuleType = type;
            this.parameters.selectedModuleCompany = this.parameters.modules[moduleIndex].company;

            this.parameters.selectedModule.moduleStatus = this.parameters.modules[moduleIndex].moduleStatus === 'active';
            this.parameters.selectedModule.controllerStatus = this.parameters.modules[moduleIndex].controllerStatus === 'active';

            this.subtitles.getSubtitles('default', this.parameters.modules[moduleIndex].tag);
            this.styles.setStyle(style);
        } else {
            this.parameters.selectedModuleHash = 0;
            window.localStorage.removeItem('selectedModuleHash');
            this.styles.setDefaultStyle();
            this.parameters.clearModulesData();
        }
    }

    private resetLoginFormData(): void {
        this.username = '';
        this.password = '';
        this.rememberMe = false;
        this.update.lastUpdate = null;
        this.menu.downloaded = false;
    }

    private stopUpdaters(): void {
        this.update.stopInterval();
        this.popup.stopPopupInterval();
        this.database.resetDatabaseService();
        this.zones.resetZonesService();
        this.zonesSystem.resetZonesSystemService();
        this.settings.selectedModule = undefined;
    }

    private resetUserData(): void {
        this.views.hideView();
        this._authenticated = false;
        this.username = '';
        this.user.resetUser();
        this.parameters.selectedModuleHash = 0;
        this.parameters.clearLocalStorage();
        this.menu.showInputPassword(false);
        this.settingsNotifications.allPushDevices = null;
    }

    isAuthenticated(): boolean {
        if (this._authenticated) {
            return this._authenticated;
        }

        return this.authenticate()
    }

    private authenticate(): boolean {
        const token = window.localStorage.getItem('token');

        if (token) {
            this._authenticated = true;
            this.token = token;
            const tokenData = JSON.parse(window.atob(token.split('.')[1]));
            this.user.setUser(tokenData.user_id, tokenData.username);

            const urlModuleHash = this.location.path().split('/')[2];
            if (urlModuleHash === '0') {
                this.parameters.selectedModuleHash = 0;
            } else {
                const selectedModuleHash = window.localStorage.getItem('selectedModuleHash');
                this.parameters.selectedModuleHash = selectedModuleHash ?? 0;
            }
        } else {
            this._authenticated = false;
            this.token = '';
            this.user.resetUser();
            this.parameters.clearLocalStorage();
            this.parameters.clearModulesData();
        }

        return this._authenticated
    }
}
